import { Routes } from '@angular/router';
import { authGuard, leavePageGuard, noAuthGuard } from './core';
import { dataPreloadGuard } from './core/guards';
import { activeBookingGuard } from './core/guards/active-bookings/active-bookings.guard';
import { createBookingGuard } from './core/guards/booking/create-booking.guard';
import { remoteConfigGuard } from './core/guards/remote-config.guard';
import { SyncGuard } from './core/guards/sync.guard';
import { invitationGuard } from './core/guards/invitation/invitation.guard';
import { propertyEntryRedirectGuard } from './core/guards/property-entry/property-entry-redirect.guard';

export const routes: Routes = [
  {
    path: 'error',
    loadChildren: () => import('./domains/error/routes').then((m) => m.routes),
  },
  {
    path: 'login',
    loadChildren: () => import('./domains/login/routes').then((m) => m.routes),
    canActivate: [noAuthGuard],
  },
  {
    path: 'register',
    loadChildren: () =>
      import('./domains/register/routes').then((m) => m.routes),
    canActivate: [noAuthGuard],
  },
  {
    path: 'forgot-password',
    loadChildren: () =>
      import('./domains/forgot-password/routes').then((m) => m.routes),
    canActivate: [noAuthGuard],
  },
  {
    path: 'complete-profile',
    loadChildren: () =>
      import('./domains/complete-profile/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'planner',
    loadChildren: () =>
      import('./domains/planner/routes').then((m) => m.routes),
    canActivate: [
      authGuard,
      SyncGuard,
      dataPreloadGuard,
      remoteConfigGuard,
      activeBookingGuard,
    ],
    data: {
      remoteConfigKey: 'enable_ai_planner',
    },
  },
  {
    path: 'interests',
    loadChildren: () =>
      import('./domains/interests/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'qr-scanner',
    loadChildren: () =>
      import('./domains/qr-scanner/routes').then((m) => m.routes),
  },
  {
    path: 'building',
    loadChildren: () =>
      import('./domains/building/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'no-locations',
    loadChildren: () =>
      import('./domains/no-locations/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'booking/new',
    loadChildren: () =>
      import('./domains/booking-form/routes').then((m) => m.routes),
    canMatch: [createBookingGuard],
  },
  {
    path: 'booking/new/:propertyId',
    loadChildren: () =>
      import('./domains/booking-form/routes').then((m) => m.routes),
    canActivate: [authGuard, propertyEntryRedirectGuard],
  },
  {
    path: 'payment/:serviceRequestId/:clientSecret',
    loadChildren: () =>
      import('./domains/payment/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard, dataPreloadGuard],
  },
  {
    path: 'payment/:serviceRequestId/:clientSecret/:customerSessionClientSecret',
    loadChildren: () =>
      import('./domains/payment/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard, dataPreloadGuard],
  },
  {
    path: 'categories',
    loadChildren: () =>
      import('./domains/categories/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard],
  },
  {
    path: 'service-kind/:serviceKind',
    loadComponent: () =>
      import(
        './domains/categories/pages/services-by-category/services-by-category.page'
      ).then((m) => m.ServicesByCategoryPage),
    canActivate: [authGuard, SyncGuard, dataPreloadGuard],
    canDeactivate: [leavePageGuard],
  },
  {
    path: 'services',
    loadChildren: () =>
      import('./domains/services/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard],
  },
  {
    path: 'reservations',
    loadChildren: () =>
      import('./domains/reservations/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard, dataPreloadGuard],
  },
  {
    path: 'property-entry',
    loadChildren: () =>
      import('./domains/property-entry/routes').then((m) => m.routes),
  },
  {
    path: 'identity',
    loadChildren: () =>
      import('./domains/identity/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard, dataPreloadGuard],
  },
  {
    path: 'check-in',
    loadChildren: () =>
      import('./domains/check-in/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard],
  },
  {
    path: 'notifications',
    loadChildren: () =>
      import('./domains/notifications/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'notifications-settings',
    loadComponent: () =>
      import(
        './domains/notifications/pages/notifications-settings/notifications-settings.page'
      ).then((m) => m.NotificationsSettingsPage),
    canActivate: [authGuard],
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./domains/profile/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'account',
    loadChildren: () =>
      import('./domains/account/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'family',
    loadComponent: () =>
      import('./domains/family/pages/family/family.page').then(
        (m) => m.FamilyPage,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'billing-address',
    loadComponent: () =>
      import(
        './domains/billing-address/pages/billing-address/billing-address.page'
      ).then((m) => m.BillingAddressPage),
    canActivate: [authGuard],
  },
  {
    path: 'wallet',
    loadChildren: () => import('./domains/wallet/routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    path: 'property',
    loadChildren: () =>
      import('./domains/property/routes').then((m) => m.routes),
    canActivate: [authGuard, SyncGuard, dataPreloadGuard],
  },
  {
    path: '',
    loadChildren: () => import('./domains/tabs/routes').then((m) => m.routes),
    canActivate: [authGuard, invitationGuard, SyncGuard],
  },
];
